// Right-side drawer that renders the step form(s) for the selected
// "What changed?" category, reusing the shared RightPanel (MUI right Drawer).
//
// The drawer edits a LOCAL DRAFT copy of the form values. Nothing is written
// back to the real Formik state (and therefore nothing shows up in the
// "Changes Requested" summary) until the user clicks "Save". Closing the
// drawer any other way (X / backdrop) discards the draft.

import React from "react";
import { Box, Button, Stack } from "@mui/material";
import produce from "immer";
import { RightPanel } from "../../../../../../../../components/RightPanel/RightPanel";
import { Dialog } from "../../../../../../../../components/Dialogs/Dialog";
import { IAutomobilePolicyChangeProps } from "../../Steps/AutomobilePolicyChange.types";
import { IAutomobilePolicyState } from "../../../../../../../../redux/automobilePolicyChangeReq/automobilePolicyChangeReq.types";
import { NameChangeInformation } from "../../Steps/NameChange";
import { AddressChangeInformationV2 } from "./AddressChangeInformationV2";
import { DriverInformationV2 } from "./DriverInformationV2";
import { DeletePickerV2 } from "./DeletePickerV2";
import { CreditCardInformationV2 } from "./CreditCardInformationV2";
import { CancellationReinstatementV2 } from "./CancellationReinstatementV2";
import { IChangeSelection } from "./WhatChangedBoard";

interface IChangeSectionDrawerProps extends IAutomobilePolicyChangeProps {
  selection: IChangeSelection | null;
  onClose: () => void;
}

// Sets a possibly-dotted path (e.g. "credit_card_info.expiry") on an immer
// draft, creating intermediate objects as needed.
const setPath = (obj: any, path: string, value: any) => {
  const keys = path.split(".");
  let cursor = obj;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    if (cursor[key] === null || cursor[key] === undefined) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }
  cursor[keys[keys.length - 1]] = value;
};

export const ChangeSectionDrawer: React.FC<IChangeSectionDrawerProps> = ({
  selection,
  onClose,
  ...stepProps
}) => {
  // Local draft of the form values, seeded from the real values each time a
  // new selection opens the drawer.
  const [draftValues, setDraftValues] = React.useState<IAutomobilePolicyState>(
    stepProps.values
  );

  React.useEffect(() => {
    if (selection) {
      setDraftValues(stepProps.values);
    }
     
  }, [selection]);

  if (!selection) {
    return null;
  }

  // Draft-backed Formik-like handlers passed down to the section components so
  // their edits stay local until "Save".
  const draftSetValues = (
    update: React.SetStateAction<IAutomobilePolicyState>
  ) => {
    setDraftValues((prev) =>
      typeof update === "function"
        ? (update as (p: IAutomobilePolicyState) => IAutomobilePolicyState)(prev)
        : update
    );
  };

  const draftSetFieldValue = (field: string, value: any) => {
    setDraftValues((prev) =>
      produce(prev, (d: any) => {
        setPath(d, field, value);
      })
    );
  };

  const draftHandleChange = (e: React.ChangeEvent<any>) => {
    const target = e?.target;
    if (!target) {
      return;
    }
    const field = target.name || target.id;
    if (!field) {
      return;
    }
    const value =
      target.type === "checkbox" ? target.checked : target.value;
    draftSetFieldValue(field, value);
  };

  const draftProps: IAutomobilePolicyChangeProps = {
    ...stepProps,
    values: draftValues,
    setValues: draftSetValues as IAutomobilePolicyChangeProps["setValues"],
    setFieldValue:
      draftSetFieldValue as IAutomobilePolicyChangeProps["setFieldValue"],
    handleChange: draftHandleChange as IAutomobilePolicyChangeProps["handleChange"],
  };

  const handleSave = () => {
    // Commit the draft to the real Formik state, then close.
    stepProps.setValues(draftValues);
    onClose();
  };

  const renderSection = () => {
    switch (selection.section) {
      case "nameChange":
        return <NameChangeInformation {...draftProps} />;
      case "addressChange":
        return <AddressChangeInformationV2 {...draftProps} />;
      case "driverChange":
        // Dedicated Driver drawer: the mode (Add/Delete) chosen on the board is
        // forwarded and, for Add, the driver form auto-opens so the user lands
        // directly on it.
        return (
          <DriverInformationV2
            {...draftProps}
            mode={selection.mode}
            autoOpen
          />
        );
      case "paymentMethodChange":
        // Payment method is a single-object section (credit card / bank
        // details). Its fields are edited in the drawer draft and surface in
        // "Changes Requested" only after "Save".
        return <CreditCardInformationV2 {...draftProps} hideHeading />;
      case "cancellationReinstatement":
        // Cancellation / Reinstatement: Cancel Reason + Subtype. Single-object
        // section that writes cancellation_reason + sub_type on Save.
        return (
          <CancellationReinstatementV2 {...draftProps} mode={selection.mode} />
        );
      case "vehicleChange":
      case "coverageChange":
      case "interestedPartyChange":
        // Record sections only reach the drawer for "Delete" (Add opens the
        // record panel directly). Show just the delete picker.
        return <DeletePickerV2 {...draftProps} section={selection.section} />;
      case "multipleChange":
        return (
          <>
            <NameChangeInformation {...draftProps} />
            <AddressChangeInformationV2 {...draftProps} />
            <DriverInformationV2
              {...draftProps}
              mode={draftValues.driver_change.type}
              // Multiple Change renders in a Dialog (z-index 1300); open the
              // Add Driver panel above it so it isn't hidden behind the dialog.
              driverPanelZIndex={1500}
            />
          </>
        );
      default:
        return null;
    }
  };

  // AddressChangeInformationV2 is purpose-built for the drawer's width (its
  // own responsive Grid breakpoints already fit), so it's excluded from the
  // full-row flattening applied to the shared V1 steps below.
  const isAddressChangeSection =
    selection.section === "addressChange" || selection.section === "multipleChange";

  // The Payment Method section lays its fields out in a single horizontal row
  // (per the mockup), so it also opts out of the full-row flattening and uses
  // a wider panel.
  const isPaymentSection = selection.section === "paymentMethodChange";

  // Sections whose own Grid breakpoints are intended to be kept as-is (not
  // flattened to one field per row) and that want the wider panel.
  const isWideSection = isAddressChangeSection || isPaymentSection;

  // Field content shared by both the drawer and the dialog presentation.
  const sectionContent = (
    <Box
      sx={{
        // Hide the shared step's own blue LabelCard heading bar — the
        // drawer/dialog header already shows the section title. The LabelCard
        // renders as the first (Box) child of each shared step, while the field
        // grid is a .MuiGrid-root, so this only hits the bar.
        "& > .MuiBox-root:first-of-type": { display: "none" },
        // Normalise the shared step's field grid for the narrow drawer: each
        // field takes a full row so nothing gets squeezed into a thin column.
        // (The shared steps use small md/lg widths tuned for a full-page form.)
        "& .MuiGrid-container": {
          rowGap: 0.5,
        },
        ...(!isWideSection && {
          "& .MuiGrid-container > .MuiGrid-item": {
            flexBasis: "100%",
            maxWidth: "100%",
          },
        }),
      }}
    >
      {renderSection()}
    </Box>
  );

  // Multiple Change is presented as a centered dialog; every other section
  // uses the right-side panel.
  if (selection.section === "multipleChange") {
    return (
      <Dialog
        open={true}
        size="lg"
        fullWidth
        title={selection.heading}
        subtitle="Update the fields for this change, then click Save to add it to the request."
        onClose={onClose}
        actions={[
          {
            type: "button",
            variant: "contained",
            label: "Save",
            onClick: handleSave,
          },
        ]}
      >
        {sectionContent}
      </Dialog>
    );
  }

  return (
    <RightPanel
      open={true}
      version="v2"
      width={isWideSection ? "70%" : "42%"}
      heading={selection.heading}
      subHeading="Update the fields for this change, then click Save to add it to the request."
      onClose={onClose}
      drawerProps={{ zIndex: 1400 }}
      actionButtons={() => (
        <Stack
          direction="row"
          justifyContent="flex-start"
          spacing={2}
          sx={{
            position: "relative",
            zIndex: (theme) => theme.zIndex.modal + 2,
            pb: 1,
          }}
        >
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      )}
    >
      {sectionContent}
    </RightPanel>
  );
};
